/**
 * Modal "Nouvelle séance d'émargement" (Rafik 02/06/2026, Phase 3).
 *
 * Sélection formateur + apprenant → POST /v2/emargement/seances → callback.
 * Pour la V1 admin : on liste les formateurs depuis l'endpoint Apprenants
 * (qui contient formateurId) → liste unique. À terme : endpoint dédié
 * /v2/formateurs avec stats.
 */
import { useEffect, useState } from 'react';
import { X, Loader2, AlertCircle, CheckCircle2, Copy } from 'lucide-react';

const API_BASE =
  typeof window !== 'undefined' && window.location.host.startsWith('cockpit-v2.')
    ? 'https://cockpit.andalous-elearning.com/api/v1'
    : '/api/v1';

interface FormateurOpt {
  id: string;
  nom: string;
  prenom: string;
  email: string;
}

interface ApprenantOpt {
  id: string;
  nom: string;
  prenom: string;
  email: string;
  statutZoho: string;
  formateurId: string | null;
}

interface CreatedSeance {
  id: string;
  token: string;
  destinataireNom: string;
  secondaryDestinataireNom: string | null;
}

export function NouvelleSeanceModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [formateurs, setFormateurs] = useState<FormateurOpt[]>([]);
  const [apprenants, setApprenants] = useState<ApprenantOpt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFormateur, setSelectedFormateur] = useState<string>('');
  const [selectedApprenant, setSelectedApprenant] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedSeance | null>(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/v2/lookups/formateurs`, { credentials: 'include' }).then((r) => r.json()),
      fetch(`${API_BASE}/v2/apprenants`, { credentials: 'include' }).then((r) => r.json()),
    ])
      .then(([fRes, aRes]) => {
        const fData = (fRes?.data?.formateurs ?? fRes?.data ?? []) as FormateurOpt[];
        const aData = (aRes?.data?.apprenants ?? aRes?.data ?? []) as ApprenantOpt[];
        setFormateurs(fData);
        setApprenants(aData);
        setIsLoading(false);
      })
      .catch((e) => {
        setError(`Chargement impossible : ${(e as Error).message}`);
        setIsLoading(false);
      });
  }, []);

  // Filtre les apprenants visibles selon le formateur sélectionné
  const filteredApprenants = selectedFormateur
    ? apprenants.filter((a) => a.formateurId === selectedFormateur)
    : apprenants;

  const handleSubmit = async () => {
    if (!selectedFormateur || !selectedApprenant) return;
    const formateur = formateurs.find((f) => f.id === selectedFormateur);
    const apprenant = apprenants.find((a) => a.id === selectedApprenant);
    if (!formateur || !apprenant) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/v2/emargement/seances`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formateurZohoId: formateur.id,
          formateurNomComplet: `${formateur.prenom} ${formateur.nom}`.trim(),
          formateurEmail: formateur.email,
          apprenantZohoId: apprenant.id,
          apprenantNomComplet: `${apprenant.prenom} ${apprenant.nom}`.trim(),
          apprenantEmail: apprenant.email,
        }),
      });
      const json = (await r.json()) as {
        data: {
          id: string;
          token: string;
          destinataireNom: string;
          secondaryDestinataireNom: string | null;
        } | null;
        error: { message?: string } | null;
      };
      if (!r.ok || !json.data) {
        throw new Error(json.error?.message ?? `HTTP ${r.status}`);
      }
      setCreated(json.data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
          <h2 className="text-lg font-bold text-neutral-900">
            {created ? 'Séance créée ✓' : "Nouvelle séance d'émargement"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {created ? (
          <div className="px-6 py-6">
            <div className="mb-4 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
              <div className="text-sm text-emerald-900">
                <div className="font-semibold">Séance créée</div>
                <div className="mt-1">
                  Formateur : <strong>{created.destinataireNom}</strong>
                  <br />
                  Élève : <strong>{created.secondaryDestinataireNom ?? '—'}</strong>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-600">
                Lien formateur (phase 1)
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded bg-white px-2 py-1 text-xs text-neutral-700">
                  {window.location.origin}/emargements/by-token/{created.token}
                </code>
                <button
                  type="button"
                  onClick={() =>
                    navigator.clipboard.writeText(
                      `${window.location.origin}/emargements/by-token/${created.token}`,
                    )
                  }
                  className="flex items-center gap-1 rounded border border-neutral-300 bg-white px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-100"
                >
                  <Copy className="h-3 w-3" />
                  Copier
                </button>
              </div>
              <p className="mt-2 text-xs text-neutral-500">
                Le lien élève sera généré une fois que le formateur aura soumis sa partie.
              </p>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={onCreated}
                className="rounded-lg bg-[#0a2540] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0a2540]/90"
              >
                Fermer
              </button>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center gap-2 p-12 text-sm text-neutral-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement…
          </div>
        ) : (
          <div className="px-6 py-6">
            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-neutral-600">
                  Formateur
                </label>
                <select
                  value={selectedFormateur}
                  onChange={(e) => {
                    setSelectedFormateur(e.target.value);
                    setSelectedApprenant('');
                  }}
                  className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-[#0a2540] focus:outline-none"
                >
                  <option value="">— Sélectionner —</option>
                  {formateurs.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.prenom} {f.nom}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-neutral-600">
                  Élève {selectedFormateur && `(filtré sur ${filteredApprenants.length})`}
                </label>
                <select
                  value={selectedApprenant}
                  onChange={(e) => setSelectedApprenant(e.target.value)}
                  disabled={!selectedFormateur}
                  className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-[#0a2540] focus:outline-none disabled:bg-neutral-100 disabled:text-neutral-400"
                >
                  <option value="">— Sélectionner —</option>
                  {filteredApprenants.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.prenom} {a.nom} [{a.statutZoho}]
                    </option>
                  ))}
                </select>
                {selectedFormateur && filteredApprenants.length === 0 && (
                  <p className="mt-1 text-xs text-amber-700">
                    Aucun élève rattaché à ce formateur côté Apprenant.Suivi_par.
                  </p>
                )}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-neutral-200 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!selectedFormateur || !selectedApprenant || isSubmitting}
                className="flex items-center gap-2 rounded-lg bg-[#0a2540] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0a2540]/90 disabled:opacity-50"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Créer la séance
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
